import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AvisClientsCarousel from '../components/AvisClientsCarousel';
import ProtectedImage from '../components/ProtectedImage';
import { getVehicleById, getVehiclesPublic } from '../services/api';

function getVideoInfo(url) {
  if (!url) return null;

  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return { type: 'iframe', src: `https://www.youtube.com/embed/${ytMatch[1]}` };
  if (url.includes('youtube.com/embed/')) return { type: 'iframe', src: url };

  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };

  const tiktokMatch = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (tiktokMatch) return { type: 'iframe', src: `https://www.tiktok.com/embed/v2/${tiktokMatch[1]}` };
  if (url.includes('tiktok.com/embed/')) return { type: 'iframe', src: url };

  // Fichier vidéo uploadé
  return { type: 'video', src: url };
}


function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0, protected: true });
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [degatsExpanded, setDegatsExpanded] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [videoPopup, setVideoPopup] = useState({ open: false, index: 0 });
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false);
  const [reviewPopupPlaying, setReviewPopupPlaying] = useState(false);
  const [tiktokReviewThumb, setTiktokReviewThumb] = useState(null);
  const [vehicleTiktokPopupOpen, setVehicleTiktokPopupOpen] = useState(false);
  const [vehicleTiktokPopupPlaying, setVehicleTiktokPopupPlaying] = useState(false);
  const [tiktokVehicleThumb, setTiktokVehicleThumb] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', message: '' });
  const [similarVehicles, setSimilarVehicles] = useState([]);
  const descRef = useRef(null);
  const optionsRef = useRef(null);
  const degatsRef = useRef(null);

  const getVisibleCount = () => {
    if (window.innerWidth < 640) return 2;
    if (window.innerWidth < 1024) return 4;
    return 6;
  };
  const [visibleCount, setVisibleCount] = useState(getVisibleCount);
  useEffect(() => {
    const handler = () => setVisibleCount(getVisibleCount());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    getVehicleById(id)
      .then(res => {
        setVehicle(res.data);
        setSelectedImage(res.data.mainImageUrl);
        if (res.data.tiktokReviewUrl) {
          fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(res.data.tiktokReviewUrl)}`)
            .then(r => r.json())
            .then(data => { if (data.thumbnail_url) setTiktokReviewThumb(data.thumbnail_url); })
            .catch(() => {});
        }
        if (res.data.tiktokVehicleUrl) {
          fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(res.data.tiktokVehicleUrl)}`)
            .then(r => r.json())
            .then(data => { if (data.thumbnail_url) setTiktokVehicleThumb(data.thumbnail_url); })
            .catch(() => {});
        }
        return getVehiclesPublic().then(all => {
          const similar = all.data
            .filter(v => v.marque === res.data.marque && v.id !== res.data.id && v.statut === 'EN_LIGNE')
            .slice(0, 10);
          setSimilarVehicles(similar);
        });
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [id]);

  const openLightbox = (images, index, isProtected = true) => setLightbox({ open: true, images, index, protected: isProtected });
  const closeLightbox = () => setLightbox({ open: false, images: [], index: 0, protected: true });
  const prevImage = () => setLightbox(prev => ({ ...prev, index: prev.index === 0 ? prev.images.length - 1 : prev.index - 1 }));
  const nextImage = () => setLightbox(prev => ({ ...prev, index: prev.index === prev.images.length - 1 ? 0 : prev.index + 1 }));

  useEffect(() => {
    const handleKey = (e) => {
      if (!lightbox.open) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox.open]);


  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-yellow-500 text-xl">Chargement...</p>
    </div>
  );

  if (!vehicle) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-black">Véhicule non trouvé.</p>
    </div>
  );

  const reduction = vehicle.prixPromo && vehicle.prix
    ? Math.round(((vehicle.prix - vehicle.prixPromo) / vehicle.prix) * 100)
    : null;

  const isVendu = vehicle.statut === 'VENDU';
  const vehicleUrl = `${window.location.origin}/vehicle/${vehicle.id}`;
  const whatsappMessage = `Bonjour, je suis intéressé par le ${vehicle.marque} ${vehicle.modele}.\n\nLien du véhicule : ${vehicleUrl}`;
  const whatsappUrl = `https://wa.me/33647474068?text=${encodeURIComponent(whatsappMessage)}`;

  const mainImages = vehicle.images || [];
  const expertiseImages = vehicle.expertiseImages || [];

  let parsedOptions = [];
  try { parsedOptions = vehicle.options ? JSON.parse(vehicle.options) : []; } catch {}

  let parsedDegats = [];
  try { parsedDegats = vehicle.degats ? JSON.parse(vehicle.degats) : []; } catch {}

  let parsedVideos = [];
  try { parsedVideos = vehicle.videoUrls ? JSON.parse(vehicle.videoUrls) : []; } catch {}


  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* Lightbox */}
      {lightbox.open && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center" onClick={closeLightbox}>
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white text-4xl font-bold hover:text-yellow-500 transition z-50">&times;</button>
          <div className="relative flex items-center justify-center w-full h-full px-16" onClick={e => e.stopPropagation()}>
            <button onClick={prevImage} className="absolute left-4 text-white text-5xl font-bold hover:text-yellow-500 transition bg-black/50 rounded-full w-14 h-14 flex items-center justify-center z-20">&#8249;</button>
            <div className="relative inline-flex items-center justify-center">
              {lightbox.protected ? (
                <ProtectedImage src={lightbox.images[lightbox.index]?.imageUrl || lightbox.images[lightbox.index]} alt=""
                  className="max-h-[75vh] max-w-full rounded-xl shadow-2xl" style={{ objectFit: 'contain' }} />
              ) : (
                <img src={lightbox.images[lightbox.index]?.imageUrl || lightbox.images[lightbox.index]} alt=""
                  className="max-h-[75vh] max-w-full rounded-xl shadow-2xl" style={{ objectFit: 'contain' }} />
              )}
              {isVendu && lightbox.protected && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div style={{ transform: 'rotate(-20deg)', background: 'rgba(234,179,8,0.18)', padding: '10px 28px', borderRadius: '12px' }}>
                    <span className="font-extrabold tracking-widest select-none"
                      style={{ fontSize: '3rem', color: 'rgba(0,0,0,0.30)', letterSpacing: '0.25em', display: 'block' }}>
                      VENDU
                    </span>
                  </div>
                </div>
              )}
            </div>
            <button onClick={nextImage} className="absolute right-4 text-white text-5xl font-bold hover:text-yellow-500 transition bg-black/50 rounded-full w-14 h-14 flex items-center justify-center z-20">&#8250;</button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 px-4 mt-4 max-w-full" onClick={e => e.stopPropagation()}>
            {lightbox.images.map((img, i) => (
              <img key={i} src={img?.imageUrl || img} alt=""
                onClick={() => setLightbox(prev => ({ ...prev, index: i }))}
                className={`w-16 h-12 object-cover rounded-lg cursor-pointer flex-shrink-0 transition border-2 ${i === lightbox.index ? 'border-yellow-500' : 'border-transparent opacity-60 hover:opacity-100'}`} />
            ))}
          </div>
          <p className="text-gray-400 text-sm mt-2">{lightbox.index + 1} / {lightbox.images.length}</p>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 pt-12 pb-0">
        <button onClick={() => navigate(-1)} className="text-yellow-500 hover:text-yellow-400 transition mb-8 inline-block font-semibold">
          ← Retour aux véhicules
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-4">
          {/* Galerie */}
          <div className="order-1">
            <div className="rounded-2xl overflow-hidden bg-gray-100 mb-4 relative cursor-pointer" style={{ height: '500px' }}
              onClick={() => mainImages.length > 0 && openLightbox(mainImages, mainImages.findIndex(img => img.imageUrl === selectedImage))}>
              {selectedImage ? (
                <ProtectedImage src={selectedImage} alt={vehicle.marque} className="w-full h-full hover:scale-105 transition-transform duration-500" style={{ objectFit: 'cover' }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-6xl">🚗</div>
              )}
              {isVendu && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div style={{ transform: 'rotate(-20deg)', background: 'rgba(234,179,8,0.18)', padding: '10px 28px', borderRadius: '12px' }}>
                    <span className="font-extrabold tracking-widest select-none"
                      style={{ fontSize: '3rem', color: 'rgba(0,0,0,0.30)', letterSpacing: '0.25em', display: 'block' }}>
                      VENDU
                    </span>
                  </div>
                </div>
              )}
              {mainImages.length > 1 && (
                <div className="absolute bottom-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                  {mainImages.length} photos
                </div>
              )}
            </div>
            {mainImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {mainImages.map((img) => (
                  <div key={img.id} className="relative flex-shrink-0 cursor-pointer" onClick={() => setSelectedImage(img.imageUrl)}>
                    <img src={img.imageUrl} alt=""
                      className={`w-24 h-20 object-cover rounded-xl transition border-2 ${selectedImage === img.imageUrl ? 'border-yellow-500 border-4' : 'border-gray-200 hover:border-yellow-300'}`} />
                    {isVendu && (
                      <div className="absolute inset-0 rounded-xl flex items-center justify-center pointer-events-none overflow-hidden">
                        <div style={{ transform: 'rotate(-20deg)', background: 'rgba(234,179,8,0.18)', padding: '2px 6px', borderRadius: '4px' }}>
                          <span className="font-extrabold select-none"
                            style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.30)', letterSpacing: '0.1em', display: 'block' }}>
                            VENDU
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

          </div>

          {/* Infos */}
          <div className="order-2 flex flex-col justify-between">
            <div>
              {/* Badges */}
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                {isVendu ? (
                  <span className="px-4 py-1 rounded-full text-sm font-bold bg-yellow-500 text-black">Vendu</span>
                ) : (
                  <>
                    <span className={`px-4 py-1 rounded-full text-sm font-bold ${vehicle.etat === 'Neuf' ? 'bg-yellow-500 text-black' : 'bg-gray-100 text-gray-600'}`}>{vehicle.etat}</span>
                    {vehicle.disponibilite && (
                      <span className={`px-4 py-1 rounded-full text-sm font-bold ${vehicle.disponibilite === 'IMMEDIAT' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}`}>
                        {vehicle.disponibilite === 'IMMEDIAT' ? 'Disponible immédiatement' : 'En arrivage'}
                      </span>
                    )}
                  </>
                )}
              </div>

              <h1 className="text-black text-4xl font-extrabold mb-4">{vehicle.marque} {vehicle.modele}</h1>

              {/* Prix */}
              {!isVendu && (
                <div className="mb-6 space-y-1">
                  {/* HT — prix principal en grand */}
                  {vehicle.prix && (
                    <div className="flex items-center gap-3 flex-wrap">
                      {vehicle.prixPromo ? (
                        <>
                          <span className="text-yellow-500 text-4xl font-bold">{vehicle.prixPromo.toLocaleString()} € HT</span>
                          <span className="text-gray-400 text-2xl line-through">{vehicle.prix.toLocaleString()} €</span>
                          {reduction && <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">-{reduction}%</span>}
                        </>
                      ) : (
                        <span className="text-yellow-500 text-4xl font-bold">{vehicle.prix.toLocaleString()} € HT</span>
                      )}
                    </div>
                  )}
                  {/* TTC — affiché en secondaire */}
                  {vehicle.prixTtc && (
                    <div className="flex items-center gap-3 flex-wrap">
                      {vehicle.prixPromoTtc ? (
                        <>
                          <span className="text-gray-600 text-xl font-semibold">{vehicle.prixPromoTtc.toLocaleString()} € TTC</span>
                          <span className="text-gray-400 text-base line-through">{vehicle.prixTtc.toLocaleString()} €</span>
                        </>
                      ) : (
                        <span className="text-gray-600 text-xl font-semibold">{vehicle.prixTtc.toLocaleString()} € TTC</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Caractéristiques */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: 'Mise en circulation', value: vehicle.dateMiseEnCirculation },
                  { label: 'Kilométrage', value: vehicle.kilometrage ? vehicle.kilometrage.toLocaleString() + ' km' : '-' },
                  { label: 'Carburant', value: vehicle.carburant },
                  { label: 'Transmission', value: vehicle.transmission },
                  { label: 'Moteur', value: vehicle.moteur },
                  { label: 'Couleur', value: vehicle.couleur },
                  ...(vehicle.puissance != null ? [{ label: 'Puissance', value: vehicle.puissance + ' ch' }] : []),
                  ...(vehicle.puissanceAdmin != null ? [{ label: 'Puissance admin.', value: vehicle.puissanceAdmin + ' CV' }] : []),
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">{item.label}</p>
                    <p className="text-black font-semibold">{item.value || '-'}</p>
                  </div>
                ))}
              </div>

            </div>

            {/* Boutons contact */}
            {!isVendu && (
              <div className="flex flex-col gap-3 mt-4">
                <a href="tel:+33647474068" className="bg-yellow-500 text-black text-center py-4 rounded-xl font-bold text-lg hover:bg-yellow-400 transition shadow-md">
                  Appeler pour ce véhicule
                </a>
                <a href={whatsappUrl} className="border-2 border-yellow-500 text-yellow-600 text-center py-4 rounded-xl font-bold hover:bg-yellow-500 hover:text-black transition">
                  Contacter par WhatsApp
                </a>
              </div>
            )}
            {isVendu && (
              <div className="hidden lg:block bg-blue-50 border border-blue-200 rounded-xl p-6 text-center mt-4">
                <p className="text-blue-700 font-bold text-lg mb-1">Ce véhicule a trouvé son propriétaire</p>
                <p className="text-blue-500 text-sm">Consultez nos autres véhicules disponibles</p>
                <Link to="/" className="mt-4 inline-block bg-yellow-500 text-black px-6 py-3 rounded-xl font-bold hover:bg-yellow-400 transition">
                  Voir nos véhicules
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {vehicle.description && (
          <div className="mt-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-7 bg-yellow-500 rounded-full flex-shrink-0" />
              <h3 className="text-black font-bold text-xl">Description</h3>
            </div>
            <p ref={descRef} className={`text-gray-600 leading-relaxed text-sm whitespace-pre-line ${!descExpanded ? 'line-clamp-6' : ''}`}>
              {vehicle.description}
            </p>
            {(vehicle.description.length > 300 || descExpanded) && (
              <button onClick={() => setDescExpanded(!descExpanded)}
                className="mt-3 inline-flex items-center gap-1.5 text-yellow-600 font-semibold text-sm hover:text-yellow-500 transition">
                {descExpanded ? '▲ Réduire' : '▼ Lire la suite'}
              </button>
            )}
          </div>
        )}

        {/* Options */}
        {parsedOptions.length > 0 && (
          <div className="mt-10" ref={optionsRef}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-7 bg-yellow-500 rounded-full flex-shrink-0" />
              <h3 className="text-black font-bold text-xl">Équipements <span className="text-yellow-500">&</span> options</h3>
              <span className="bg-yellow-500 text-black text-xs font-extrabold px-2.5 py-1 rounded-full">{parsedOptions.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0.5">
              {(optionsExpanded ? parsedOptions : parsedOptions.slice(0, visibleCount)).map((opt, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-yellow-50 transition-colors border border-transparent hover:border-yellow-100 group">
                  <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    {opt.icon
                      ? <span className="text-xs leading-none">{opt.icon}</span>
                      : <svg className="w-3.5 h-3.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                    }
                  </div>
                  <span className="text-gray-700 text-sm font-medium">{opt.text}</span>
                </div>
              ))}
            </div>
            {parsedOptions.length > visibleCount && (
              <button onClick={() => {
                if (optionsExpanded) optionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setOptionsExpanded(!optionsExpanded);
              }} className="mt-3 inline-flex items-center gap-1.5 text-yellow-600 font-semibold text-sm hover:text-yellow-500 transition">
                {optionsExpanded ? '▲ Voir moins' : `▼ Voir les ${parsedOptions.length - visibleCount} autres options`}
              </button>
            )}
          </div>
        )}

        {/* Photos expertise — carousel */}
        {expertiseImages.length > 0 && (
          <div className="mt-12">
            <h3 className="text-black font-bold text-2xl mb-2">Photos <span className="text-yellow-500">expertise</span></h3>
            <p className="text-gray-400 mb-6 text-sm">Inspection détaillée du véhicule</p>
            <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#eab308 #f3f4f6' }}>
              {expertiseImages.map((img, index) => (
                <div key={img.id} className="flex-shrink-0 w-72 h-52 rounded-2xl overflow-hidden border border-gray-200 shadow-sm cursor-pointer hover:shadow-xl transition-all duration-300 group"
                  onClick={() => openLightbox(expertiseImages, index, false)}>
                  <img src={img.imageUrl} alt="Photo expertise"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dégats */}
        {parsedDegats.length > 0 && (
          <div className="mt-12" ref={degatsRef}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-7 bg-red-500 rounded-full flex-shrink-0" />
              <h3 className="text-black font-bold text-xl">Dégats <span className="text-red-500">constatés</span></h3>
              <span className="bg-red-500 text-white text-xs font-extrabold px-2.5 py-1 rounded-full">{parsedDegats.length}</span>
            </div>
            <p className="text-gray-400 mb-4 text-sm -mt-2">État du véhicule à la vente</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(degatsExpanded ? parsedDegats : parsedDegats.slice(0, visibleCount)).map((d, i) => (
                <div key={i} className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  <span className="text-lg flex-shrink-0">{d.icon || '⚠️'}</span>
                  <span className="text-gray-700 text-sm font-medium">{d.text}</span>
                </div>
              ))}
            </div>
            {parsedDegats.length > visibleCount && (
              <button onClick={() => {
                if (degatsExpanded) degatsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setDegatsExpanded(!degatsExpanded);
              }} className="mt-3 inline-flex items-center gap-1.5 text-red-500 font-semibold text-sm hover:text-red-400 transition">
                {degatsExpanded ? '▲ Voir moins' : `▼ Voir les ${parsedDegats.length - visibleCount} autres dégats`}
              </button>
            )}
          </div>
        )}

        {/* Vidéos — carousel */}
        {parsedVideos.length > 0 && (
          <div className="mt-12">
            <h3 className="text-black font-bold text-2xl mb-2">Vidéos <span className="text-yellow-500">du véhicule</span></h3>
            <p className="text-gray-400 mb-6 text-sm">Cliquez pour regarder</p>
            <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#eab308 #f3f4f6' }}>
              {parsedVideos.map((url, index) => {
                const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');
                const isTiktok = url.includes('tiktok.com');
                const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                const ytThumb = ytMatch ? `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg` : null;
                return (
                  <div key={index} className="flex-shrink-0 w-72 h-52 relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm cursor-pointer hover:shadow-xl transition-all duration-300 group"
                    onClick={() => setVideoPopup({ open: true, index })}>
                    {ytThumb ? (
                      <img src={ytThumb} alt={`Vidéo ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : isTiktok ? (
                      <div className="w-full h-full bg-black flex flex-col items-center justify-center gap-3">
                        <span className="text-5xl">🎵</span>
                        <span className="text-white font-bold text-sm">TikTok</span>
                      </div>
                    ) : (
                      <video src={url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" preload="metadata" muted />
                    )}
                    <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-black/60 border-2 border-white/60 flex items-center justify-center group-hover:bg-yellow-500 group-hover:border-yellow-500 transition-all duration-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    {isYoutube && <span className="absolute bottom-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded">YouTube</span>}
                    {isTiktok && <span className="absolute bottom-2 left-2 bg-black text-white text-xs font-bold px-2 py-0.5 rounded border border-white/20">TikTok</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Formulaire WhatsApp — uniquement pour les véhicules disponibles */}
        {!isVendu && (
          <div className="bg-white rounded-3xl border border-gray-100 p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

              {/* Colonne gauche — texte */}
              <div>
                <p className="text-yellow-500 text-sm font-bold uppercase tracking-widest mb-3">Une question ?</p>
                <h2 className="text-4xl font-extrabold text-black leading-tight mb-4">
                  On répond à <span className="text-yellow-500">toutes</span> vos questions
                </h2>
                <p className="text-gray-500 text-lg leading-relaxed mb-8">
                  Vous vous demandez si ce véhicule est disponible dans une autre couleur ? Si une remise est possible ? Si la livraison est envisageable ? Écrivez-nous, nous répondons rapidement.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: '🎨', title: 'Autre couleur disponible ?', desc: 'Demandez-nous, nous avons peut-être d\'autres exemplaires en stock.' },
                    { icon: '💬', title: 'Réponse sous 2h en journée', desc: 'Notre équipe est disponible 7j/7 pour répondre à vos questions.' },
                    { icon: '📸', title: 'Plus de photos disponibles', desc: 'Demandez-nous, on vous envoie toutes les photos du véhicule directement sur WhatsApp.' },
                    { icon: '🚀', title: 'Livraison France, Algérie, Tunisie', desc: 'Nous livrons partout. Votre véhicule arrive chez vous.' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-yellow-50 border border-yellow-100 flex items-center justify-center text-xl flex-shrink-0">{item.icon}</div>
                      <div>
                        <p className="font-bold text-black text-sm">{item.title}</p>
                        <p className="text-gray-400 text-sm leading-snug">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Colonne droite — formulaire */}
              <div className="bg-gray-50 rounded-3xl border border-gray-100 p-8 space-y-4">
                <h3 className="font-extrabold text-black text-xl mb-1">Nous écrire pour ce véhicule</h3>
                <p className="text-gray-400 text-sm mb-4">Le lien du {vehicle.marque} {vehicle.modele} sera inclus automatiquement.</p>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Votre prénom</label>
                  <input
                    type="text"
                    placeholder="Jean Dupont"
                    value={contactForm.name}
                    onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yellow-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Votre téléphone</label>
                  <input
                    type="tel"
                    placeholder="+33 6 xx xx xx xx"
                    value={contactForm.phone}
                    onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yellow-500 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Votre message</label>
                  <textarea
                    rows={4}
                    placeholder={`Bonjour, je suis intéressé par le ${vehicle.marque} ${vehicle.modele}. J'aimerais savoir si...`}
                    value={contactForm.message}
                    onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full border border-gray-200 bg-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yellow-500 transition resize-none"
                  />
                </div>
                <a
                  href={`https://wa.me/33647474068?text=${encodeURIComponent(
                    `Bonjour Royal Cars,\n\nJe suis ${contactForm.name || 'un client'}${contactForm.phone ? ` (${contactForm.phone})` : ''}.\n\n${contactForm.message || `Je suis intéressé par le ${vehicle.marque} ${vehicle.modele}.`}\n\n🔗 Lien du véhicule : ${window.location.origin}/vehicle/${vehicle.id}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-yellow-500 text-black py-4 rounded-xl font-bold text-base hover:bg-yellow-400 transition shadow-md"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Envoyer via WhatsApp
                </a>
                <p className="text-center text-xs text-gray-400">Aucun compte requis. Votre message s'ouvre directement dans WhatsApp.</p>
              </div>

            </div>
          </div>
        )}

        {/* Vidéo TikTok du véhicule */}
        {vehicle.tiktokVehicleUrl && (() => {
          const info = getVideoInfo(vehicle.tiktokVehicleUrl);
          if (!info) return null;
          return (
            <div className="bg-gray-50 rounded-3xl p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

                {/* Texte gauche */}
                <div>
                  <p className="text-yellow-500 text-sm font-bold uppercase tracking-widest mb-2">Notre TikTok</p>
                  <h3 className="text-black font-extrabold text-3xl leading-tight mb-4">
                    Retrouvez ce véhicule sur <span className="text-yellow-500">TikTok</span> !
                  </h3>
                  <p className="text-gray-600 text-base leading-relaxed mb-4">
                    Découvrez ce véhicule sous toutes ses coutures grâce à nos vidéos exclusives : présentation intérieur/extérieur, motorisation, finitions et essais sur route. Chez <span className="font-semibold text-black">Royal Cars</span>, on filme tout pour que vous puissiez vous projeter avant même de venir le voir !
                  </p>
                  <p className="text-gray-600 text-base leading-relaxed mb-6">
                    📱 <span className="font-semibold text-black">Abonnez-vous à notre TikTok</span> pour être les premiers à voir nos nouvelles arrivées. Likez, commentez et partagez pour soutenir notre équipe et ne jamais rater une bonne affaire !
                  </p>
                  <button
                    onClick={() => { setVehicleTiktokPopupOpen(true); setVehicleTiktokPopupPlaying(false); }}
                    className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl font-bold hover:bg-gray-800 transition"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                    </svg>
                    Voir la vidéo TikTok
                  </button>
                </div>

                {/* Carte TikTok droite */}
                <div className="flex justify-center lg:justify-end">
                  <div
                    className="relative rounded-3xl overflow-hidden cursor-pointer group shadow-2xl flex-shrink-0 border border-white/10"
                    style={{ width: '240px', height: '420px' }}
                    onClick={() => { setVehicleTiktokPopupOpen(true); setVehicleTiktokPopupPlaying(false); }}
                  >
                    {tiktokVehicleThumb ? (
                      <img src={tiktokVehicleThumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-gray-800 to-gray-900">
                        <svg viewBox="0 0 24 24" className="w-16 h-16 text-white opacity-50" fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                        </svg>
                        <span className="text-gray-400 text-sm">Vidéo TikTok</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30 group-hover:from-black/60 transition-all duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center group-hover:bg-yellow-500 group-hover:border-yellow-500 transition-all duration-300 backdrop-blur-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="bg-black/80 backdrop-blur text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/20">TikTok</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Popup TikTok véhicule */}
              {vehicleTiktokPopupOpen && (
                <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4"
                  style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
                  onClick={() => { setVehicleTiktokPopupOpen(false); setVehicleTiktokPopupPlaying(false); }}>
                  <div className="relative flex flex-col items-center w-full max-w-sm" onClick={e => e.stopPropagation()}>
                    <button onClick={() => { setVehicleTiktokPopupOpen(false); setVehicleTiktokPopupPlaying(false); }}
                      className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white text-xl font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition">×</button>
                    <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-gray-900" style={{ aspectRatio: '9/16', maxHeight: '75vh' }}>
                      {vehicleTiktokPopupPlaying ? (
                        <iframe src={info?.src} title="Vidéo véhicule TikTok" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
                      ) : (
                        <>
                          {tiktokVehicleThumb ? (
                            <img src={tiktokVehicleThumb} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-900">
                              <svg viewBox="0 0 24 24" className="w-20 h-20 text-white opacity-30" fill="currentColor">
                                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                              </svg>
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center" onClick={() => setVehicleTiktokPopupPlaying(true)}>
                            <div className="w-20 h-20 rounded-full bg-black/60 border-2 border-white/60 flex items-center justify-center hover:bg-yellow-500 hover:border-yellow-500 transition cursor-pointer">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-9 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}


        {/* Popup vidéo avec navigation */}
        {videoPopup.open && (
          <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
            onClick={() => setVideoPopup({ open: false, index: 0 })}
          >
            <div className="relative flex flex-col items-center w-full max-w-4xl" onClick={e => e.stopPropagation()}>
              {/* Fermer */}
              <button
                onClick={() => setVideoPopup({ open: false, index: 0 })}
                className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white text-xl font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition"
              >×</button>

              {/* Lecteur */}
              {(() => {
                const url = parsedVideos[videoPopup.index];
                const info = getVideoInfo(url);
                return info?.type === 'video' ? (
                  <div className="w-full rounded-2xl overflow-hidden shadow-2xl bg-black">
                    <video src={info.src} controls autoPlay className="w-full max-h-[75vh]" />
                  </div>
                ) : (
                  <div className="w-full aspect-video rounded-2xl overflow-hidden shadow-2xl">
                    <iframe src={info?.src} title="Vidéo"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen className="w-full h-full" />
                  </div>
                );
              })()}

              {/* Navigation prev / next */}
              {parsedVideos.length > 1 && (
                <div className="flex items-center gap-4 mt-5">
                  <button
                    onClick={() => setVideoPopup(p => ({ ...p, index: p.index === 0 ? parsedVideos.length - 1 : p.index - 1 }))}
                    className="w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white text-2xl font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition"
                  >&#8249;</button>
                  <span className="text-gray-400 text-sm">{videoPopup.index + 1} / {parsedVideos.length}</span>
                  <button
                    onClick={() => setVideoPopup(p => ({ ...p, index: p.index === parsedVideos.length - 1 ? 0 : p.index + 1 }))}
                    className="w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white text-2xl font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition"
                  >&#8250;</button>
                </div>
              )}

              {/* Miniatures en bas */}
              {parsedVideos.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                  {parsedVideos.map((url, i) => {
                    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                    const thumb = ytMatch ? `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg` : null;
                    return (
                      <div key={i} onClick={() => setVideoPopup(p => ({ ...p, index: i }))}
                        className={`w-20 h-14 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer border-2 transition ${i === videoPopup.index ? 'border-yellow-500' : 'border-transparent opacity-50 hover:opacity-100'}`}>
                        {thumb
                          ? <img src={thumb} alt="" className="w-full h-full object-cover" />
                          : <video src={url} className="w-full h-full object-cover" preload="metadata" muted />
                        }
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Avis TikTok client — pour les véhicules vendus */}
      {isVendu && vehicle.tiktokReviewUrl && (() => {
        const info = getVideoInfo(vehicle.tiktokReviewUrl);
        if (!info) return null;
        return (
          <div className="bg-white py-8 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

                {/* Texte gauche */}
                <div>
                  <h2 className="text-black font-extrabold text-4xl leading-tight mb-3">
                    Un client <span className="text-yellow-500">satisfait</span>
                  </h2>
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => <span key={i} className="text-yellow-400 text-2xl">★</span>)}
                  </div>
                  <p className="text-gray-500 text-lg leading-relaxed mb-5">
                    Nos clients parlent d'eux-mêmes. Regardez leur expérience avec <span className="text-black font-semibold">Royal Cars</span> — une livraison, une confiance, une satisfaction.
                  </p>

                  {/* Infos véhicule vendu */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-black text-base">🚗</span>
                    </div>
                    <div>
                      <p className="text-black font-bold">{vehicle.marque} {vehicle.modele}</p>
                      <p className="text-gray-400 text-sm">Vendu avec succès · Client satisfait</p>
                    </div>
                  </div>

                  <Link to="/about#avis-clients"
                    className="inline-flex items-center gap-2 bg-yellow-500 text-black px-5 py-2.5 rounded-xl font-bold hover:bg-yellow-400 transition">
                    Voir tous nos avis clients <span>→</span>
                  </Link>
                </div>

                {/* Carte TikTok droite */}
                <div className="flex justify-center lg:justify-end">
                  <div
                    className="relative rounded-3xl overflow-hidden cursor-pointer group shadow-2xl flex-shrink-0 border border-white/10"
                    style={{ width: '240px', height: '420px' }}
                    onClick={() => setReviewPopupOpen(true)}
                  >
                    {tiktokReviewThumb ? (
                      <img src={tiktokReviewThumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-gray-800 to-gray-900">
                        <svg viewBox="0 0 24 24" className="w-16 h-16 text-white opacity-50" fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                        </svg>
                        <span className="text-gray-400 text-sm">Vidéo TikTok</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/30 group-hover:from-black/60 transition-all duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white/60 flex items-center justify-center group-hover:bg-yellow-500 group-hover:border-yellow-500 transition-all duration-300 backdrop-blur-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center justify-between">
                        <span className="bg-black/80 backdrop-blur text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/20">TikTok</span>
                        <span className="text-yellow-400 text-sm font-bold">★★★★★</span>
                      </div>
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-yellow-500 text-black text-xs font-extrabold px-3 py-1 rounded-full">VENDU</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Popup */}
            {reviewPopupOpen && (
              <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-4"
                style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
                onClick={() => { setReviewPopupOpen(false); setReviewPopupPlaying(false); }}>
                <div className="relative flex flex-col items-center w-full max-w-sm" onClick={e => e.stopPropagation()}>
                  <button onClick={() => { setReviewPopupOpen(false); setReviewPopupPlaying(false); }}
                    className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 text-white text-xl font-bold flex items-center justify-center hover:bg-yellow-500 hover:text-black transition">×</button>
                  <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-gray-900" style={{ aspectRatio: '9/16', maxHeight: '75vh' }}>
                    {reviewPopupPlaying ? (
                      <iframe src={info?.src} title="Avis client" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
                    ) : (
                      <>
                        {tiktokReviewThumb ? (
                          <img src={tiktokReviewThumb} alt="Avis client" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-black">
                            <svg viewBox="0 0 24 24" className="w-20 h-20 text-white opacity-40" fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.78a4.85 4.85 0 01-1.01-.09z"/>
                            </svg>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center cursor-pointer" onClick={() => setReviewPopupPlaying(true)}>
                          <div className="w-20 h-20 rounded-full bg-white/20 border-2 border-white/80 flex items-center justify-center hover:bg-yellow-500 hover:border-yellow-500 transition-all duration-300 backdrop-blur-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Bloc "véhicule vendu" — mobile uniquement, après la section avis client */}
      {isVendu && (
        <div className="lg:hidden px-4 pb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center max-w-7xl mx-auto">
            <p className="text-blue-700 font-bold text-lg mb-1">Ce véhicule a trouvé son propriétaire</p>
            <p className="text-blue-500 text-sm">Consultez nos autres véhicules disponibles</p>
            <Link to="/" className="mt-4 inline-block bg-yellow-500 text-black px-6 py-3 rounded-xl font-bold hover:bg-yellow-400 transition">
              Voir nos véhicules
            </Link>
          </div>
        </div>
      )}

      {/* Véhicules similaires — même marque */}
      {similarVehicles.length > 0 && (
        <div className="bg-gray-50 border-t border-gray-100 py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-yellow-500 text-sm font-bold uppercase tracking-widest mb-1">Même marque</p>
                <h2 className="text-black font-extrabold text-3xl">
                  Autres <span className="text-yellow-500">{vehicle.marque}</span>
                </h2>
              </div>
              <Link to="/" className="text-gray-500 hover:text-yellow-500 font-semibold text-sm transition flex items-center gap-1">
                Voir tous nos véhicules <span className="text-lg">→</span>
              </Link>
            </div>

            <div className="flex gap-5 overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin', scrollbarColor: '#eab308 #f3f4f6' }}>
              {similarVehicles.map(v => {
                const red = v.prixPromo && v.prix ? Math.round(((v.prix - v.prixPromo) / v.prix) * 100) : null;
                return (
                  <Link
                    key={v.id}
                    to={`/vehicle/${v.id}`}
                    onClick={() => { sessionStorage.setItem('scrollToVehicle', v.id); window.scrollTo(0, 0); }}
                    className="flex-shrink-0 w-56 rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="relative h-40 overflow-hidden bg-gray-100">
                      {v.mainImageUrl ? (
                        <ProtectedImage src={v.mainImageUrl} alt={`${v.marque} ${v.modele}`}
                          className="w-full h-full group-hover:scale-105 transition-transform duration-500" style={{ objectFit: 'cover' }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl">🚗</div>
                      )}
                      <div className="absolute top-2.5 left-2.5 flex gap-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${v.etat === 'Neuf' ? 'bg-yellow-500 text-black' : 'bg-white/90 text-gray-700 border border-gray-200'}`}>
                          {v.etat}
                        </span>
                        {red && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            -{red}%
                          </span>
                        )}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>

                    <div className="p-4">
                      <h4 className="font-bold text-black text-sm leading-tight">{v.marque} {v.modele}</h4>
                      <p className="text-gray-400 text-xs mt-1">
                        {v.dateMiseEnCirculation}{v.kilometrage ? ` · ${v.kilometrage.toLocaleString()} km` : ''}
                      </p>
                      <div className="mt-2.5 border-t border-gray-100 pt-2.5">
                        {v.prixPromo ? (
                          <div>
                            <p className="text-yellow-500 font-bold text-base leading-tight">{v.prixPromo.toLocaleString()} € HT</p>
                            <p className="text-gray-400 text-xs line-through">{v.prix.toLocaleString()} €</p>
                          </div>
                        ) : (
                          <p className="text-yellow-500 font-bold text-base">{v.prix ? v.prix.toLocaleString() + ' € HT' : '—'}</p>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Barre d'action fixe en bas — mobile uniquement */}
      {!isVendu && (
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white border-t border-gray-200 shadow-2xl flex gap-0" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <a href="tel:+33647474068" className="flex-1 flex flex-col items-center justify-center py-3 bg-yellow-500 text-black font-bold text-sm gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            Appeler
          </a>
          <a
            href={`https://wa.me/33647474068?text=${encodeURIComponent(`Bonjour Royal Cars,\n\nJe suis intéressé par le ${vehicle.marque} ${vehicle.modele}.\n\n🔗 Lien du véhicule : ${window.location.origin}/vehicle/${vehicle.id}`)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex-1 flex flex-col items-center justify-center py-3 bg-green-500 text-white font-bold text-sm gap-1"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </a>
        </div>
      )}

      <AvisClientsCarousel excludeId={vehicle?.id} />
      <Footer />
    </div>
  );
}

export default VehicleDetail;
